// Opportunity Radar (§45-47): /api/admin/rcc/radar/* on the external backend.
import { api, errMsg } from "@/lib/api";
export { useSI as useQ, isHttp } from "@/lib/sourcing";

export const R = "/admin/rcc/radar";
export const RP = "/admin/rcc/radar"; // web path prefix
export const rPost = (path, body) => api.post(`${R}${path}`, body).then((r) => r.data);
export const rPatch = (path, body) => api.patch(`${R}${path}`, body).then((r) => r.data);
export const rPut = (path, body) => api.put(`${R}${path}`, body).then((r) => r.data);
export const rDel = (path) => api.delete(`${R}${path}`).then((r) => r.data);
export const rErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  return e?.response?.status === 404 ? "This isn't live on the backend yet (backend-spec §45-47)." : errMsg(e);
};
export const rCode = (e) => e?.response?.data?.detail?.code;
export const qs = (o) => { const p = Object.entries(o).filter(([, v]) => v !== "" && v != null && v !== false); return p.length ? `?${p.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&")}` : ""; };
export const pretty = (s) => String(s ?? "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
export const ago = (d) => {
  if (!d) return "";
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};
export const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Denver";

export const CATEGORY = {
  hot: ["Hot", "border-[#F97316]/50 bg-[#7C2D12]/40 text-[#FDBA74]"],
  high_potential: ["High potential", "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F3C649]"],
  developing: ["Developing", "border-[#3B82F6]/50 bg-[#1E3A8A]/30 text-[#93C5FD]"],
  monitor: ["Monitor", "border-[#3F3F46] bg-[#27272A]/60 text-zinc-300"],
  not_qualified: ["Not qualified", "border-[#27272A] bg-transparent text-zinc-500"],
};
export const PRIORITY = {
  act_now: ["Act now", "border-[#EF4444]/50 bg-[#7F1D1D]/40 text-[#FCA5A5]"],
  this_week: ["This week", "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F3C649]"],
  nurture: ["Nurture", "border-[#3F3F46] bg-[#27272A]/60 text-zinc-400"],
};
export const STATUS = {
  not_run: ["Not run", "border-[#3F3F46] text-zinc-400"], queued: ["Queued", "border-[#8224e3]/50 text-[#d7b6ff]"],
  running: ["Running", "border-[#D4AF37]/50 text-[#F3C649]"], done: ["Done", "border-[#10B981]/50 text-[#34D399]"],
  partial: ["Partial", "border-amber-500/50 text-amber-300"], failed: ["Failed", "border-red-500/50 text-red-300"],
};
export const STAGES = [
  ["new_lead", "New lead"], ["researching", "Researching"], ["qualified", "Qualified"], ["contact_identified", "Contact identified"],
  ["outreach_active", "Outreach active"], ["contacted", "Contacted"], ["engaged", "Engaged"], ["meeting_scheduled", "Meeting scheduled"],
  ["discovery_completed", "Discovery completed"], ["proposal_requested", "Proposal requested"], ["proposal_sent", "Proposal sent"],
  ["negotiation", "Negotiation"], ["closed_won", "Won"], ["closed_lost", "Lost"], ["nurture", "Nurture"],
];
export const stageLabel = (s) => (STAGES.find(([k]) => k === s) || [s, pretty(s || "new_lead")])[1];
export const SENIORITY = { c_level: "C-level", founder: "C-level", vp: "VP", director: "Director", head: "Director", manager: "Manager", talent: "Talent", recruiter: "Talent" };
export const SEN_ORDER = ["c_level", "founder", "vp", "head", "director", "manager", "talent", "recruiter"];
export const STEPS = [
  ["research", "AI company research summary (cited)"],
  ["signals", "Hiring, funding, leadership and layoff news from the last year (with sources)"],
  ["open_roles", "Open roles from their careers page and Greenhouse/Lever/Ashby/Workable"],
  ["decision_makers", "Current VPs, directors, heads of, founders, hiring managers and in-house recruiters (+ guessed work emails)"],
  ["score", "Opportunity score and category"],
  ["brief", "Why-now brief: angle, best first contact, talking points, recommended services"],
];
export const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
export const hourLabel = (h) => `${((Number(h) + 11) % 12) + 1}:00 ${Number(h) < 12 ? "AM" : "PM"}`;
export const EV_STATUS = {
  verified: ["Verified", "border-[#10B981]/50 bg-[#064E3B]/40 text-[#34D399]"],
  likely_valid: ["Likely valid", "border-amber-500/50 bg-amber-500/10 text-amber-300"],
  invalid: ["Invalid", "border-red-500/50 bg-red-500/10 text-red-300"],
  pending: ["Pending · re-checking", "border-[#3F3F46] bg-[#27272A]/60 text-zinc-400"],
  greylisted: ["Pending · re-checking", "border-[#3F3F46] bg-[#27272A]/60 text-zinc-400"],
};
export const SAMPLE_CSV = "Company,Website,Locations,Description,Primary Industry,Size,Type,LinkedIn URL,Funding,Founded\nAcme Robotics,acmerobotics.com,Denver CO; Austin TX,Warehouse robots,Robotics,51-200,Private,https://www.linkedin.com/company/acme-robotics,Series B,2018\n";
export const heatColor = (h) => (h >= 90 ? "#F97316" : h >= 70 ? "#D4AF37" : h >= 40 ? "#3B82F6" : "#3F3F46");
